import { useTranslation } from 'react-i18next';
import { IdCardIcon, PersonIcon } from '@/shared/ui';
import { BuildingIcon } from '@/shared/ui/components/basic/icons';
import { SectionCard } from '@/features/property-management/components/PropertyDetailView/SectionCard';
import type { ClientRow } from '../../context/ClientManagementContext';
import { ContactRow } from './helpers';

interface OverviewProfileSectionsProps {
  client: ClientRow;
}

const MARITAL_KEY: Record<string, string> = {
  single: 'drawer.personalExtra.maritalOptions.single',
  married: 'drawer.personalExtra.maritalOptions.married',
  divorced: 'drawer.personalExtra.maritalOptions.divorced',
  widowed: 'drawer.personalExtra.maritalOptions.widowed',
  stable_union: 'drawer.personalExtra.maritalOptions.stableUnion',
};

const PROOF_KEY: Record<string, string> = {
  holerite: 'drawer.financial.proofTypes.holerite',
  irpf: 'drawer.financial.proofTypes.irpf',
  extrato: 'drawer.financial.proofTypes.extrato',
  pro_labore: 'drawer.financial.proofTypes.proLabore',
  outro: 'drawer.financial.proofTypes.outro',
};

/**
 * Formats monthly income as Brazilian Real currency.
 *
 * @param value - Amount in whole reais.
 * @returns Formatted BRL string.
 */
function formatIncome(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

/**
 * Whether the client has any complementary personal/identity fields.
 *
 * @param client - Client row.
 */
function hasComplementary(client: ClientRow): boolean {
  return [
    client.nationality,
    client.profession,
    client.gender,
    client.education,
    client.job_title,
    client.employer,
    client.industry,
    client.marital_status,
    client.rg,
    client.issuing_agency,
    client.trade_name,
    client.corporate_name,
  ].some(Boolean);
}

/**
 * Whether the client has address fields worth showing.
 *
 * @param client - Client row.
 */
function hasAddress(client: ClientRow): boolean {
  return Boolean(client.address);
}

/**
 * Whether the client has financial fields.
 *
 * @param client - Client row.
 */
function hasFinancial(client: ClientRow): boolean {
  return client.monthly_income != null || Boolean(client.income_proof);
}

/**
 * The client's complementary identity fields, as its own card.
 *
 * Split out of one three-card block so the overview can pair personal
 * with address and financial with preferences, side by side.
 *
 * @param props - Client row with mapped detail extras.
 * @returns The personal card, or nothing when no field is filled.
 */
export function PersonalExtraCard({ client }: OverviewProfileSectionsProps) {
  const { t } = useTranslation('features/clients');
  const maritalLabel = client.marital_status
    ? t(MARITAL_KEY[client.marital_status] ?? client.marital_status, {
      defaultValue: client.marital_status,
    })
    : '';

  return (
    <>
      {hasComplementary(client) ? (
        <SectionCard title={t('drawer.personalExtra.title')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--space-3)]">
            {client.gender ? (
              <ContactRow icon={<PersonIcon width={16} height={16} />} label={t('drawer.personalExtra.gender')} value={t(`attributes.gender.${client.gender}`, { defaultValue: client.gender })} />
            ) : null}
            {maritalLabel ? (
              <ContactRow icon={<PersonIcon width={16} height={16} />} label={t('drawer.personalExtra.maritalStatus')} value={maritalLabel} />
            ) : null}
            {client.nationality ? (
              <ContactRow icon={<PersonIcon width={16} height={16} />} label={t('drawer.personalExtra.nationality')} value={client.nationality} />
            ) : null}
            {client.education ? (
              <ContactRow icon={<PersonIcon width={16} height={16} />} label={t('drawer.personalExtra.education')} value={t(`attributes.education.${client.education}`, { defaultValue: client.education })} />
            ) : null}
            {client.profession ? (
              <ContactRow icon={<PersonIcon width={16} height={16} />} label={t('drawer.personalExtra.profession')} value={client.profession} />
            ) : null}
            {client.job_title ? (
              <ContactRow icon={<PersonIcon width={16} height={16} />} label={t('drawer.personalExtra.jobTitle')} value={client.job_title} />
            ) : null}
            {client.employer ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.personalExtra.employer')} value={client.employer} />
            ) : null}
            {client.industry ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.personalExtra.industry')} value={client.industry} />
            ) : null}
            {client.rg ? (
              <ContactRow icon={<IdCardIcon width={16} height={16} />} label={t('drawer.identityExtra.rg')} value={client.rg} />
            ) : null}
            {client.issuing_agency ? (
              <ContactRow icon={<IdCardIcon width={16} height={16} />} label={t('drawer.identityExtra.issuingAgency')} value={client.issuing_agency} />
            ) : null}
            {client.corporate_name ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.identityExtra.corporateName')} value={client.corporate_name} />
            ) : null}
            {client.trade_name ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.identityExtra.tradeName')} value={client.trade_name} />
            ) : null}
          </div>
        </SectionCard>
      ) : null}
    </>
  );
}

/**
 * The client's address, as its own card.
 *
 * @param props - The client row.
 * @returns The address card, or nothing when no address is on file.
 */
export function AddressCard({ client }: OverviewProfileSectionsProps) {
  const { t } = useTranslation('features/clients');
  const fields = client.address_fields;

  return (
    <>
      {hasAddress(client) ? (
        <SectionCard title={t('drawer.address.title')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--space-3)]">
            {fields?.zip_code ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.address.zipCode')} value={fields.zip_code} />
            ) : null}
            {fields?.street ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.address.street')} value={fields.street} />
            ) : null}
            {fields?.number ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.address.number')} value={fields.number} />
            ) : null}
            {fields?.complement ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.address.complement')} value={fields.complement} />
            ) : null}
            {fields?.neighborhood ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.address.neighborhood')} value={fields.neighborhood} />
            ) : null}
            {fields?.city ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.address.city')} value={fields.city} />
            ) : null}
            {fields?.state ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.address.state')} value={fields.state} />
            ) : null}
            {!fields?.street && client.address ? (
              <ContactRow icon={<BuildingIcon width={16} height={16} />} label={t('drawer.address.title')} value={client.address} />
            ) : null}
          </div>
        </SectionCard>
      ) : null}
    </>
  );
}

/**
 * The client's declared income and proof of it, as its own card.
 *
 * @param props - The client row.
 * @returns The financial card, or nothing when neither field is set.
 */
export function FinancialCard({ client }: OverviewProfileSectionsProps) {
  const { t } = useTranslation('features/clients');
  const proofLabel = client.income_proof
    ? t(PROOF_KEY[client.income_proof] ?? client.income_proof, {
      defaultValue: client.income_proof,
    })
    : '';

  return (
    <>
      {hasFinancial(client) ? (
        <SectionCard title={t('drawer.financial.title')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--space-3)]">
            {client.monthly_income != null ? (
              <ContactRow
                icon={<IdCardIcon width={16} height={16} />}
                label={t('drawer.financial.monthlyIncome')}
                value={formatIncome(client.monthly_income)}
              />
            ) : null}
            {proofLabel ? (
              <ContactRow
                icon={<IdCardIcon width={16} height={16} />}
                label={t('drawer.financial.incomeProof')}
                value={proofLabel}
              />
            ) : null}
          </div>
        </SectionCard>
      ) : null}
    </>
  );
}
